import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import type { InvestorsPermissionSet } from "./types";

export const userEmailSelector = (state: RootState) => state.user.email;

export const permissionsSelector = createSelector([(state: RootState) => state.user.permissions], (permissions) =>
  distinctBy(permissions, (p) => p.clientCode)
);

export const sessionClientCodeSelector = (state: RootState): string | undefined => state.user.sessionClientCode;

export const identityLoadingStateSelector = (state: RootState) => state.user.loading.state;
export const identityUserIdSelector = (state: RootState) => state.user.identity?.id || "";
export const identitySignInEmailSelector = (state: RootState) => state.user.identity?.signInEmail || "";
export const identityAvatarImageSelector = (state: RootState) => state.user.identity?.avatarImage || "";
export const identityLargeImageSelector = (state: RootState) => state.user.identity?.largeImage;

export const loadingIdentitySelector = (state: RootState) => state.user.loading.identity;

export const userConsentAgreementsSelector = createSelector(
  [(state: RootState) => state.user.identity?.consentAgreements],
  (consentAgreements) => consentAgreements ?? []
);

export const impersonationSelector = (state: RootState) => state.user.isImpersonationMode;

export const impersonatedAccessToAllEntitiesSelector = (state: RootState) =>
  state.user.impersonatedAccessToAllEntities !== false;

export const clientCodeSelector = (state: RootState) => state.user.clientCode;

const investorPermissionsSelector = createSelector(
  [(state: RootState) => state.user.permissions, (state: RootState) => state.user.clientCode],
  (permissions, clientCode) =>
    permissions.filter((p) => p.clientCode === clientCode).flatMap((p) => p.investorsPermissions)
);

export const investorsWithSettingsAccessSelector = createSelector([investorPermissionsSelector], (investors) =>
  investors.filter((i) => i.investorLevelPermissions.includes("ManageInvestorSettings"))
);

export const entityPermissionsSelector = createSelector(
  [(state: RootState) => state.user.permissions, (state: RootState) => state.user.clientCode],
  (permissions, clientCode) =>
    permissions.filter((p) => p.clientCode === clientCode).flatMap((p) => p.entityPermissions)
);

export const workspaceInvestorSelector = (state: RootState): InvestorsPermissionSet | undefined => {
  const investors = investorsWithSettingsAccessSelector(state);
  const { selectedInvestorId } = state.user;
  if (!selectedInvestorId) {
    return investors[0];
  }

  return investors.find((i) => i.investorId === selectedInvestorId) ?? investors[0];
};
